import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { type PartnerCallbackConfiguration } from 'domains/connect/api'
import { Input } from 'gin-and-tonic/common/input/Input'
import { Select } from 'legacy/dash-components/select/Select'
import { useFormErrorMessageMap } from 'utils/react-hook-form/reactHookForm.hooks'
import {
  containerStyles,
  fieldsetStyle,
  legendStyle,
  labelStyle,
  fieldRowStyle,
  inputLabelStyle,
  inputOuterStyles,
  formStyle,
} from 'domains/connect/components/styles'
import { Button } from 'gin-and-tonic/common/button/Button'
import { useCallbackTestingForm } from 'domains/connect/components/utils/callbackTestingForm.hooks'
import { CallbackTestingResponse } from 'domains/connect/components/CallbackTestingResponse'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { type SimpleOption } from 'legacy/dash-blocks/dash-select/utils/select.types'
import { useDashMessage } from 'intl/intl.hooks'

const callbackTestingFormStyle = css`
  ${formStyle};
  margin-bottom: ${spacing.X12};
`

interface Props {
  stagingConfiguration: PartnerCallbackConfiguration
}

export const CallbackTestingForm: FunctionComponent<Props> = ({ stagingConfiguration }) => {
  const {
    onSubmit,
    errors,
    orderIdFieldRef,
    eventName,
    handleEventNameChange,
    callbackResponse,
    callbackLoading,
    callbackError,
  } = useCallbackTestingForm()

  const orderIdErrorMessageId = useFormErrorMessageMap({
    required: 'forms.validations.required',
  })(errors.orderId)

  if (!stagingConfiguration) {
    throw new Error('staging configuration not present')
  }

  const messages = {
    header: useDashMessage('connect.callback-testing.send-callback'),
    orderIdField: useDashMessage('connect.callback-testing.form.fields.orderId'),
    eventNameField: useDashMessage('connect.callback-testing.form.fields.eventName'),
  }

  return (
    <div css={containerStyles}>
      <form css={callbackTestingFormStyle} onSubmit={onSubmit}>
        <fieldset css={fieldsetStyle}>
          <legend css={legendStyle}>{messages.header}</legend>
          <div css={fieldRowStyle}>
            <label css={inputLabelStyle} htmlFor="orderId">
              {messages.orderIdField}:
            </label>
            <Input
              name="orderId"
              errorMessageId={orderIdErrorMessageId}
              ref={orderIdFieldRef}
              outerStyles={inputOuterStyles}
            />
          </div>
          <div css={fieldRowStyle}>
            <label css={labelStyle} htmlFor="eventName">
              {messages.eventNameField}:
            </label>
            <Select<SimpleOption<string>>
              id="eventName"
              value={eventName}
              options={stagingConfiguration.availableStatuses.map(c => ({ label: c, value: c }))}
              onChange={handleEventNameChange}
            />
          </div>
        </fieldset>
        <Button type="submit" messageId="connect.callback-testing.form.submit" />
      </form>
      <CallbackTestingResponse
        callbackResponse={callbackResponse}
        loading={callbackLoading}
        error={callbackError}
      />
    </div>
  )
}
